import { css } from '@emotion/react';
import {
  faChevronRight,
  faKeyboard, faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type ChangeEventHandler, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Accordion, useSections,
} from '~/_shared/baseComponents/accordion';
import { getAlternatingColor } from '~/_shared/baseComponents/alternatingStripes/alternatingStripes';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import { TriStateCheckboxComponent } from '~/_shared/baseComponents/checkbox/triStateCheckbox.component';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown/regularDropdown.component';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { TextInputComponent } from '~/_shared/baseComponents/inputs/textInput/textInput.component';
import { SubTextHighlightComponent } from '~/_shared/baseComponents/subTextHighlight/subTextHighlight.component';
import { TableComponent } from '~/_shared/baseComponents/table/table.component';
import { TableCheckboxCellComponent } from '~/_shared/baseComponents/table/tableCheckboxCell.component';
import { TableDataCellComponent } from '~/_shared/baseComponents/table/tableDataCell.component';
import { TableHeaderComponent } from '~/_shared/baseComponents/table/tableHeader.component';
import { TableHeaderCellComponent } from '~/_shared/baseComponents/table/tableHeaderCell.component';
import { TableRowComponent } from '~/_shared/baseComponents/table/tableRow.component';
import { HintTooltipComponent } from '~/_shared/baseComponents/tooltip/hintTooltip.component';
import {
  MODAL_DEFAULT_MAX_CONTENT_HEIGHT_INNER,
  ModalComponent,
} from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import {
  SelectedItemComponent, Size,
} from '~/_shared/components/selectedItem/selectedItem.component';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ModalProps } from '~/modal/modalType.enum';
import { mapToolTitleMap } from '~/sidebar/sidebarApps/mapTools/mapTools.helpers';
import type { DemographicIdCode } from '~/store/boundaryGroups/boundaryGroups.repository';
import { getDemographicsRegionTranslation } from '~/store/demographics/demographics.helpers';
import type { DemographicsRegion } from '~/store/demographics/demographics.repository';
import {
  DEMOGRAPHIC_REGIONS_FOR_PREMIUM_DATA, type DemographicRegion,
} from '~/store/demographics/demographics.types';
import type { DataLayerData } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';

enum AccordionSection {
  NameGroup = 'NameGroup',
  SetVariables = 'SetVariables',
  IncludedLayers = 'IncludedLayers',
}

const ALL_CATEGORIES_VALUE = null;
const PAGE_SIZE = 25;
const MAX_SELECTED_DEMOGRAPHICS = 8;

const contentStyle = css({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  height: `calc(${MODAL_DEFAULT_MAX_CONTENT_HEIGHT_INNER})`,
});

const footerStyle = css({
  width: '100%',
});

const submitButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
});

const bodyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

const descriptionStyle = css({
  margin: 0,
  padding: '8px 16px 16px',
  fontSize: 14,
  lineHeight: 1.5,
});

const namePanelStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const nameHeadingStyle = css({
  margin: 0,
  fontSize: 20,
  fontWeight: 600,
});

const filtersRowStyle = css({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: 16,
  marginBottom: 12,
});

const filterLabelStyle = css({
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  letterSpacing: '0.05em',
});

const tableWrapperStyle = css({
  flex: 1,
  minHeight: 60,
  overflowY: 'auto',
  marginBottom: 8,
});

const belowTableTextStyle = css({
  fontSize: 13,
  marginTop: 4,
  display: 'flex',
  gap: 12,
  flexDirection: 'row',
});

const selectedLayersTextStyle = (theme: Theme) => css({
  fontWeight: 600,
  color: theme.textColors.info,
});

const selectedLayersTooManyLinkStyle = (theme: Theme) => css({
  fontWeight: 600,
  color: theme.textColors.danger,
});

const includedLayersStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

const tableHeaderCellStyleOverridesStyle = css({
  textTransform: 'uppercase',
});

const tableHeaderForCheckboxOverridesStyle = css({
  paddingRight: 0,
});

const headerCheckmarkStyle = css({
  display: 'none',
});

const tableCellOverridesStyle = css({
  height: 24,
  fontSize: '14px',
});

const headerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingRight: 12,
});

const breadcrumbsStyle = css({
  fontSize: '24px',
  fontWeight: 700,
  padding: '12px 16px 8px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
});

const tableRowOverridesStyle = (theme: Theme) => css({
  '&:nth-of-type(2n+1) > div': {
    backgroundColor: getAlternatingColor(theme),
    fontWeight: 'normal',
  },
});

const tableCheckboxOverrideStyle = css({
  height: 16,
  width: 16,
});

const accordionFlexStyle = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

const setVariablesItemFlexStyle = css({
  '&[open]': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  '&[open]::details-content': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  '&[open] [data-element="accordion-item-panel"]': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
});

const TABLE_COLUMN_WIDTHS = ['25px', '110px', '1fr', '105px', '180px'] as const;

export const DataLayerEditorModalComponent = ({
  boundaryGroupName,
  demographicsPerRegion,
  initialData,
  onSubmit,
  onClose,
  isOpen,
  isLoading,
}: ModalProps<{
  boundaryGroupName: string;
  initialData?: Partial<DataLayerData>;
  demographicsPerRegion: DemographicsRegion[];
  isLoading?: boolean;
  onSubmit: (dataLayerData: DataLayerData) => void;
}>) => {
  const [t] = useTranslation();

  const [title, setTitle] = useState(initialData?.title ?? '');
  const [selectedRegion, _] = useState<DemographicRegion>(
    initialData?.region ?? DEMOGRAPHIC_REGIONS_FOR_PREMIUM_DATA[0]
  );
  const [selectedDemographicIds, setSelectedDemographicIds] = useState<ReadonlyArray<DemographicIdCode>>(
    initialData?.demographics ?? []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(ALL_CATEGORIES_VALUE);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { isExpanded, toggleSection, collapseSection } = useSections<AccordionSection>([
    AccordionSection.NameGroup,
    AccordionSection.SetVariables,
    AccordionSection.IncludedLayers,
  ]);

  const allDemographics = useMemo(() => (
    demographicsPerRegion.find(r => r.id === selectedRegion)?.demographics ?? []
  ), [demographicsPerRegion, selectedRegion]);

  const categories = useMemo(() => (
    [...new Set(allDemographics.map(d => d.category))]
  ), [allDemographics]);

  const filteredDemographics = useMemo(() => (
    allDemographics.filter(d => {
      const matchesCategory = selectedCategory === ALL_CATEGORIES_VALUE || d.category === selectedCategory;
      const matchesSearch = !searchTerm || d.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    })
  ), [allDemographics, selectedCategory, searchTerm]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredDemographics]);

  useEffect(() => {
    if (initialData?.title) {
      collapseSection(AccordionSection.NameGroup);
    }
  }, [collapseSection, initialData?.title]);

  const visibleDemographics = useMemo(() => (
    filteredDemographics.slice(0, visibleCount)
  ), [filteredDemographics, visibleCount]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setVisibleCount(prev => Math.min(prev + PAGE_SIZE, filteredDemographics.length));
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredDemographics.length, visibleCount]);

  const selectedDemographics = useMemo(() => (
    allDemographics.filter(d => selectedDemographicIds.includes(d.id))
  ), [allDemographics, selectedDemographicIds]);

  const categoryOptions = useMemo((): ReadonlyArray<DropdownOption<string | null>> => [
    { value: ALL_CATEGORIES_VALUE, name: 'All' },
    ...categories.map(cat => ({ value: cat, name: cat })),
  ], [categories]);

  const allFilteredSelected = filteredDemographics.length > 0
    && filteredDemographics.every(d => selectedDemographicIds.includes(d.id));
  const someFilteredSelected = filteredDemographics.some(d => selectedDemographicIds.includes(d.id));
  const headerCheckmark = allFilteredSelected
    ? TriStateRange.Full
    : someFilteredSelected
      ? TriStateRange.Partial
      : TriStateRange.None;

  const overSelectedDemographics = selectedDemographicIds.length > MAX_SELECTED_DEMOGRAPHICS;
  const isFormValid = title.trim() !== '' && selectedDemographicIds.length > 0 && !overSelectedDemographics;

  const handleSelectAll: ChangeEventHandler<HTMLInputElement> = () => {
    const filteredIds = new Set(filteredDemographics.map(d => d.id));
    if (allFilteredSelected) {
      setSelectedDemographicIds(selectedDemographicIds.filter(id => !filteredIds.has(id)));
    }
    else {
      setSelectedDemographicIds([...new Set([...selectedDemographicIds, ...filteredIds])]);
    }
  };

  const handleToggleDemographic = (id: DemographicIdCode): ChangeEventHandler<HTMLInputElement> => () => {
    if (selectedDemographicIds.includes(id)) {
      setSelectedDemographicIds(selectedDemographicIds.filter(sid => sid !== id));
    }
    else {
      setSelectedDemographicIds([...selectedDemographicIds, id]);
    }
  };

  const handleRemoveDemographic = (id: DemographicIdCode) => {
    setSelectedDemographicIds(selectedDemographicIds.filter(sid => sid !== id));
  };

  const handleSubmit = () => {
    onSubmit({
      title: title.trim(),
      region: selectedRegion,
      demographics: selectedDemographicIds,
    });
  };

  const handleNameBlur = () => {
    if (title.trim() !== '') {
      collapseSection(AccordionSection.NameGroup);
    }
  };

  const getUnits = (preData: string, postData: string): string => {
    const combined = `${preData ?? ''}${postData ?? ''}`.trim();
    return combined || '-';
  };

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      contentStyle={contentStyle}
      maxWidth={1200}
      caption={t(mapToolTitleMap[MapTool.DataLayers])}
      additionalContent={isLoading ? <OverlayLoaderComponent /> : null}
      footer={(
        <div css={footerStyle}>
          <div css={submitButtonWrapperStyle}>
            <ButtonComponent
              text={t('dataLayersTool.saveAndLoadGroup')}
              isDisabled={!isFormValid}
              onClick={handleSubmit}
            />
          </div>
        </div>
      )}
    >
      <div css={bodyStyle}>
        <div css={headerStyle}>
          <div css={breadcrumbsStyle}>
            <span>{getDemographicsRegionTranslation(selectedRegion, t)}</span>
            <FontAwesomeIcon
              icon={faChevronRight}
            />
            <span>{boundaryGroupName}</span>
          </div>
          <div>
            {!isExpanded(AccordionSection.NameGroup) && (
              <HintTooltipComponent
                tooltipContent={t('dataLayersTool.editorDescription')}
                size={24}
              />
            )}
          </div>
        </div>

        {isExpanded(AccordionSection.NameGroup) && (
          <p css={descriptionStyle}>
            {t('dataLayersTool.editorDescription')}
          </p>
        )}

        <Accordion css={accordionFlexStyle}>
          <Accordion.Item
            open={isExpanded(AccordionSection.NameGroup)}
            onToggle={() => toggleSection(AccordionSection.NameGroup)}
          >
            <Accordion.ItemHeading>
              <Accordion.StepNumber />
              {t('dataLayersTool.nameYourLayerGroup').toUpperCase()}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <div css={namePanelStyle}>
                <h3 css={nameHeadingStyle}>{t('dataLayersTool.nameYourGroup')}</h3>
                <TextInputComponent
                  icon={faKeyboard}
                  value={title}
                  onBlur={handleNameBlur}
                  onChange={newValue => setTitle(newValue)}
                  placeholder={t('dataLayersTool.nameYourGroup')}
                />
              </div>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item
            open={isExpanded(AccordionSection.SetVariables)}
            onToggle={() => toggleSection(AccordionSection.SetVariables)}
            css={setVariablesItemFlexStyle}
          >
            <Accordion.ItemHeading>
              <Accordion.StepNumber />
              {t('dataLayersTool.setVariables').toUpperCase()}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <div css={filtersRowStyle}>
                <div>
                  <span css={filterLabelStyle}>{t('dataLayersTool.selectCategory').toUpperCase()}</span>
                  <RegularDropdownComponent
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                  />
                </div>
                <div>
                  <span css={filterLabelStyle}>{t('dataLayersTool.searchTerm').toUpperCase()}</span>
                  <TextInputComponent
                    icon={faMagnifyingGlass}
                    value={searchTerm}
                    onChange={newValue => setSearchTerm(newValue)}
                  />
                </div>
              </div>

              <div css={tableWrapperStyle}>
                <TableComponent columnWidths={TABLE_COLUMN_WIDTHS}>
                  <TableHeaderComponent>
                    <TableHeaderCellComponent css={tableHeaderForCheckboxOverridesStyle}>
                      <TriStateCheckboxComponent
                        css={headerCheckmarkStyle}
                        checkmark={headerCheckmark}
                        checkedSetter={handleSelectAll}
                      />
                    </TableHeaderCellComponent>
                    <TableHeaderCellComponent css={tableHeaderCellStyleOverridesStyle}>
                      {t('dataLayersTool.metric.id')}
                    </TableHeaderCellComponent>
                    <TableHeaderCellComponent css={tableHeaderCellStyleOverridesStyle}>
                      {t('dataLayersTool.metric.name')}
                    </TableHeaderCellComponent>
                    <TableHeaderCellComponent css={tableHeaderCellStyleOverridesStyle}>
                      {t('dataLayersTool.metric.units')}
                    </TableHeaderCellComponent>
                    <TableHeaderCellComponent css={tableHeaderCellStyleOverridesStyle}>
                      {t('dataLayersTool.metric.category')}
                    </TableHeaderCellComponent>
                  </TableHeaderComponent>

                  {visibleDemographics.map(demo => (
                    <TableRowComponent key={demo.id} css={tableRowOverridesStyle}>
                      <TableCheckboxCellComponent
                        checkboxStyle={tableCheckboxOverrideStyle}
                        css={tableCellOverridesStyle}
                        isChecked={selectedDemographicIds.includes(demo.id)}
                        checkedSetter={handleToggleDemographic(demo.id)}
                      />
                      <TableDataCellComponent css={tableCellOverridesStyle}>
                        {demo.id}
                      </TableDataCellComponent>
                      <TableDataCellComponent css={tableCellOverridesStyle}>
                        <SubTextHighlightComponent
                          text={demo.name}
                          highlight={searchTerm}
                        />
                      </TableDataCellComponent>
                      <TableDataCellComponent css={tableCellOverridesStyle}>
                        {getUnits(demo.preData, demo.postData)}
                      </TableDataCellComponent>
                      <TableDataCellComponent css={tableCellOverridesStyle}>
                        {demo.category}
                      </TableDataCellComponent>
                    </TableRowComponent>
                  ))}
                </TableComponent>
                <div ref={sentinelRef} />
              </div>

              <div css={belowTableTextStyle}>
                <span css={selectedLayersTextStyle}>
                  {t('dataLayersTool.selectedLayers', { count: selectedDemographicIds.length })}
                </span>
                {overSelectedDemographics && (
                  <span css={selectedLayersTooManyLinkStyle}>
                    {t('dataLayersTool.selectedLayers.overLimit', {
                      count: selectedDemographicIds.length,
                      limit: MAX_SELECTED_DEMOGRAPHICS,
                    })}
                  </span>
                )}
              </div>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item
            open={isExpanded(AccordionSection.IncludedLayers)}
            onToggle={() => toggleSection(AccordionSection.IncludedLayers)}
          >
            <Accordion.ItemHeading>
              {t('dataLayersTool.includedLayers').toUpperCase()}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <div css={includedLayersStyle}>
                {selectedDemographics.slice(0, MAX_SELECTED_DEMOGRAPHICS).map(demo => (
                  <SelectedItemComponent
                    key={demo.id}
                    onRemove={() => handleRemoveDemographic(demo.id)}
                    multiline
                    maxNumberOfLines={2}
                    size={Size.Small}
                  >
                    {demo.name}
                  </SelectedItemComponent>
                ))}
                {selectedDemographics.length > MAX_SELECTED_DEMOGRAPHICS && (
                  <span>&hellip;</span>
                )}
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </div>
    </ModalComponent>
  );
};
